import type { EntityState } from '@reduxjs/toolkit';
import type { FilterProps } from '../filters/filtersTypes';
import type { AccountingType, ReportConfiguration } from '../report-configurations/reportConfigurationsTypes';

export const GENERATED_REPORTS_REDUCER_KEY = 'generatedReportsStore';
export type GENERATED_REPORTS_REDUCER_KEY = typeof GENERATED_REPORTS_REDUCER_KEY;

// columns can be by account, date-range, payee, tag, entity (e.g. business, client, project)
export type HeaderType = 'DATE_RANGE' | 'QCS_ENTITY' | 'STRING';
export type CellType = 'AMOUNT' | 'DATE' | 'PERCENTAGE' | 'QCS_ENTITY' | 'STRING';
export type ReportType = 'SUMMARY' | 'COMPARISON' | 'TRANSACTION';
export type ReportSubType =
  | 'INCOME'
  | 'NET_INCOME'
  | 'SPENDING'
  | 'NET_WORTH'
  | 'TAX_SUMMARY'
  | 'BALANCES_OVER_TIME'
  | 'PERFORMANCE_OVER_TIME';
export type RowType =
  | 'CATEGORY'
  | 'PAYEE'
  | 'TAG'
  | 'ACCOUNT'
  | 'TIME'
  | 'BUSINESS'
  | 'CLIENT'
  | 'PROJECT'
  | 'TAX_LINE_ITEM'
  | 'TAX_SCHEDULE'
  | 'NONE';
export type ColumnType = 'PAYEE' | 'TAG' | 'ACCOUNT' | 'TIME' | 'BUSINESS' | 'CLIENT' | 'PROJECT' | 'NONE';
export type TimeStep = 'DAY' | 'WEEK' | 'TWO_WEEKS' | 'HALF_MONTH' | 'MONTH' | 'QUARTER' | 'SIX_MONTHS' | 'YEAR';
export type DateRange =
  | 'ALL_DATES'
  | 'THIS_MONTH'
  | 'THIS_QUARTER'
  | 'THIS_YEAR'
  | 'LAST_MONTH'
  | 'LAST_QUARTER'
  | 'LAST_YEAR'
  | 'MONTH_TO_DATE'
  | 'QUARTER_TO_DATE'
  | 'YEAR_TO_DATE'
  | 'LAST_7_DAYS'
  | 'LAST_14_DAYS'
  | 'LAST_2_MONTHS'
  | 'LAST_3_MONTHS'
  | 'LAST_6_MONTHS'
  | 'LAST_12_MONTHS'
  | 'CUSTOM';
export type CompareRange = DateRange | 'PRIOR_PERIOD' | 'PRIOR_YEAR_PERIOD';

export type ReportColumn = {
  id?: string;

  cellType?: CellType;

  isAggSum?: boolean;
} & (
  | {
      headerType: 'STRING';
      headerValue: {
        id: string;
        string: string;
      };
    }
  | {
      headerType: 'DATE_RANGE';
      headerValue: {
        startOn: string; // ISO Date
        endOn: string;
      };
    }
  | {
      headerType: 'QCS_ENTITY';
      headerValue: {
        qcs_entity: 'PAYEE' | 'TAG' | 'ACCOUNT' | 'BUSINESS';
        id: string;
      };
    }
);

export type ReportCell = {
  id?: string;
  columnId?: string;
  value?: string;
};

export type ReportRow = {
  id?: string;
  name?: string;
  parentId?: string;
  entityId?: string;
  childrenEntityIds?: string[];
  expectedSign?: string;
  cellData: ReportCell[];
  other: boolean;
};

export type Report = {
  configuration: ReportConfiguration;
  name: string;

  reportType: ReportType;
  reportSubType?: ReportSubType;

  // ISO Dates
  startOn: string;
  endOn: string;

  total?: string;

  rowType?: RowType;
  columnType?: ColumnType;

  rows: ReportRow[];
  columns: ReportColumn[];
};

export type EntityValue = {
  location: string;
  isGenerating: boolean;

  report?: Report | null;
  error?: any;
};

export type GeneratedReportConfig = {
  name: string;
  description?: string;

  reportSubType?: ReportSubType;

  timestep?: TimeStep;

  columnType: ColumnType;
  rowType: RowType;

  filter?: FilterProps;
  filterId?: string;

  location: string;

  usageType?: 'BUSINESS' | 'PERSONAL';
  usageTypeId?: string;

  accountingType?: AccountingType;
} & (
  | {
      dateRange: 'CUSTOM';
      startOn: string; // ISO date
      endOn: string;
    }
  | {
      dateRange: Exclude<DateRange, 'CUSTOM'>;
      startOn?: undefined;
      endOn?: undefined;
    }
) &
  (
    | ({
        reportType: 'COMPARISON';
      } & (
        | {
            compareRange: 'CUSTOM';
            compareStartOn: string; // ISO Date
            compareEndOn: string;
          }
        | {
            compareRange: Exclude<CompareRange, 'CUSTOM'>;
          }
      ))
    | {
        reportType: Exclude<ReportType, 'COMPARISON'>;
      }
  );

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [GENERATED_REPORTS_REDUCER_KEY]: EntityState<EntityValue, string>;
  }
}
